import { makeAutoObservable, runInAction } from "mobx";
import axios from "axios";
import { format } from "date-fns";
import { getAPIUrl } from "../../api/apiUtils";
import { groupIdToTitle } from "../../utils/groupTitles";
import { apiFetch } from "../../api/apiClient";

const getVkPostLink = (groupId, postId) => {
  if (!groupId || !postId) return null;
  const numericGroupId = Number(groupId);
  if (Number.isNaN(numericGroupId)) return null;
  const vkGroupId = numericGroupId > 0 ? -numericGroupId : numericGroupId;
  return `https://vk.com/wall${vkGroupId}_${postId}`;
};

const formatPostDate = (post) => {
  if (post.formatted_date) return post.formatted_date;
  if (!post.date) return "-";
  const timestamp =
    typeof post.date === "number" ? post.date * 1000 : Number(post.date);
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "yyyy-MM-dd HH:mm:ss");
};

const buildPostModalRow = (post) => {
  if (!post) return null;
  const rawGroupId = post.group_id ?? post.from_id;
  const groupId = rawGroupId != null ? String(rawGroupId) : "-";
  const postId = post.id ?? post.post_id ?? "-";

  const row = {
    "Дата": formatPostDate(post),
    "Название сообщества": groupIdToTitle[groupId] || groupId,
    "ID поста": postId,
    "Текст": post.text ?? "-",
    "Комментарии": post.comments_count ?? "-",
    "Лайки": post.likes?.count ?? "-",
    "Просмотры": post.views?.count ?? "-",
    "Ссылка на пост": getVkPostLink(groupId, postId) ?? "-",
  };

  return row;
};

class DynamicChartStore {
  data = [];
  loading = false;
  error = null;
  startDate = '';
  endDate = '';
  selectedGroupIds = [];
  cache = {}; // Кэш для графика
  cacheAll = {}; // Кэш для всех данных (экспорт)

  constructor() {
    makeAutoObservable(this);
  }

  setFilters({ startDate, endDate, selectedGroupIds }) {
    this.startDate = startDate;
    this.endDate = endDate;
    this.selectedGroupIds = selectedGroupIds;
  }

  // Ключ кэша по фильтрам
  getCacheKey(filters) {
    return JSON.stringify(filters);
  }

  async fetchData() {
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });
    try {
      if (!this.startDate || !this.endDate || this.selectedGroupIds.length === 0) {
        runInAction(() => {
          this.data = [];
          this.loading = false;
        });
        return;
      }
      const filters = {
        startDate: this.startDate,
        endDate: this.endDate,
        selectedGroupIds: this.selectedGroupIds
      };
      const cacheKey = this.getCacheKey(filters);
      if (this.cache[cacheKey]) {
        this.data = this.cache[cacheKey];
        this.loading = false;
        return;
      }
      const params = {
        community_id: this.selectedGroupIds.join(','),
        start_date: this.startDate,
        end_date: this.endDate,
        aggregate: 'true',
        period: 'day'
      };
      const url = `${getAPIUrl()}/detection/activity_log_all`;
      const response = await apiFetch(url + '?' + new URLSearchParams(params));
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      runInAction(() => {
        this.data = Array.isArray(result) ? result : (result.data || []);
        this.cache[cacheKey] = this.data;
      });
    } catch (e) {
      runInAction(() => {
        this.error = e;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  // Получение всех данных для экспорта (без пагинации)
  async fetchAllData(filtersString) {
    const filters = filtersString ? JSON.parse(filtersString) : {};
    const cacheKey = this.getCacheKey(filters) + '_all';
    if (this.cacheAll[cacheKey]) {
      return this.cacheAll[cacheKey];
    }
    if (!filters.startDate || !filters.endDate || !filters.selectedGroupIds || filters.selectedGroupIds.length === 0) {
      return [];
    }
    const params = {
      community_id: filters.selectedGroupIds.join(','),
      start_date: filters.startDate,
      end_date: filters.endDate,
      aggregate: 'true',
      period: 'day'
    };
    const url = `${getAPIUrl()}/detection/activity_log_all`;
    const response = await apiFetch(url + '?' + new URLSearchParams(params));
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    const data = Array.isArray(result) ? result : (result.data || []);
    this.cacheAll[cacheKey] = data;
    return data;
  }

  /**
   * Получает посты по выбранным группам и диапазону дат в формате,
   * подходящем для ChartPointDataModal (rows).
   */
  async fetchPostsForModal({
    groupIds,
    startDate,
    endDate,
    limit = 10000,
    page = 1,
    maxPages = 20,
  } = {}) {
    try {
      let effectiveGroups = groupIds;
      let effectiveStart = startDate;
      let effectiveEnd = endDate;

      if (
        (!effectiveGroups || !effectiveGroups.length) ||
        !effectiveStart ||
        !effectiveEnd
      ) {
        const savedFilters = localStorage.getItem("globalFilters");
        if (savedFilters) {
          try {
            const parsed = JSON.parse(savedFilters);
            if (!effectiveGroups || !effectiveGroups.length) {
              effectiveGroups = parsed?.selectedGroups || [];
            }
            if (!effectiveStart) {
              effectiveStart = parsed?.startDate;
            }
            if (!effectiveEnd) {
              effectiveEnd = parsed?.endDate;
            }
          } catch (err) {
            console.warn("Не удалось распарсить globalFilters:", err);
          }
        }
      }

      if (
        !effectiveGroups ||
        !effectiveGroups.length ||
        !effectiveStart ||
        !effectiveEnd
      ) {
        return [];
      }

      const rows = [];
      let currentPage = page;
      let remaining = null;

      for (let requestIndex = 0; requestIndex < maxPages; requestIndex++) {
        const params = {
          group_ids: effectiveGroups.join(","),
          page: currentPage,
          limit,
          startDate: effectiveStart,
          endDate: effectiveEnd,
        };

        const response = await axios.get(
          `${getAPIUrl()}/vk/collection/multiple`,
          { params }
        );

        const items = Array.isArray(response.data)
          ? response.data
          : response.data?.items || [];

        if (remaining == null) {
          const total = response.data?.count;
          if (typeof total === "number") {
            remaining = Math.max(total, 0);
          }
        }

        const pageRows = items
          .map((item) => buildPostModalRow(item))
          .filter((row) => row !== null);

        rows.push(...pageRows);

        if (remaining != null) {
          remaining -= items.length;
          if (remaining <= 0) {
            break;
          }
        }

        if (items.length < limit) {
          break;
        }

        currentPage += 1;
      }

      return rows.sort((a, b) => {
        const dateA = new Date(a["Дата"]);
        const dateB = new Date(b["Дата"]);
        return dateA - dateB;
      });
    } catch (error) {
      console.error("Ошибка при получении постов для модального окна:", error);
      return [];
    }
  }
}

export const dynamicChartStore = new DynamicChartStore();